/** Campaigns hooks. API data only when authenticated (errors → isError + empty). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CAMPAIGNS_QUERY_PREFIX, queryKeys } from "@/lib/query-client";
import type { Campaign, CampaignReport } from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import { campaignsApi } from "./campaigns.api";
import {
  toCampaign,
  toCampaignReport,
  toCampaignWriteBody,
  type CampaignInput,
} from "./campaigns.adapter";

const LIST_PARAMS = { limit: 100, sortBy: "createdAt", sortOrder: "desc" as const };

export type DataSource = "api" | "mock";

export function useCampaigns(): {
  campaigns: Campaign[];
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
  refetch: () => void;
} {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";

  const query = useQuery({
    queryKey: queryKeys.campaignsList(LIST_PARAMS),
    queryFn: () => campaignsApi.list(LIST_PARAMS),
    enabled: authed,
  });

  if (query.data)
    return {
      campaigns: query.data.data.map(toCampaign),
      isLoading: false,
      isError: false,
      source: "api",
      refetch: query.refetch,
    };
  if (query.isError)
    return {
      campaigns: [],
      isLoading: false,
      isError: true,
      source: "api",
      refetch: query.refetch,
    };
  return {
    campaigns: [],
    isLoading: query.isLoading,
    isError: false,
    source: "api",
    refetch: query.refetch,
  };
}

export function useCampaignReport(id: string | undefined): {
  report: CampaignReport | undefined;
  isLoading: boolean;
} {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";

  const query = useQuery({
    queryKey: queryKeys.campaignReport(id ?? "none"),
    queryFn: () => campaignsApi.report(id as string),
    enabled: authed && !!id,
  });

  return {
    report: query.data ? toCampaignReport(query.data) : undefined,
    isLoading: query.isLoading,
  };
}

function useInvalidateCampaigns() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: CAMPAIGNS_QUERY_PREFIX });
}

export function useCreateCampaign() {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: async (input: CampaignInput) => {
      return campaignsApi.create(toCampaignWriteBody(input));
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useLogCampaignResult() {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: async (args: {
      id: string;
      date: string;
      spentUsd: number;
      resultsCount: number;
      note?: string;
    }) => {
      const { id, ...body } = args;
      return campaignsApi.logResult(id, body);
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useStopCampaign() {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      return campaignsApi.stop(id, reason);
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useFinishCampaign() {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: async (id: string) => {
      return campaignsApi.finish(id);
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useArchiveCampaign() {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: async (id: string) => {
      return campaignsApi.archive(id);
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}
